import {MediaPlayer} from './MediaPlayer';
import { showToast } from '../../utils/toast.util';
import * as React from 'react';
import {useDropzone} from 'react-dropzone';
import {gql} from '@apollo/client';
import {client} from '../../../core/stores';
import {humanFileSize} from '../../../utils';
import {TranslateErrorDropzone} from '../../../utils/translator/TranslateErrorDropzone';
import {
  MediaBox,
  MediaPreview,
  MediaPreviewSubtitle,
  MediaGroupContainer,
  MediaPreviewContainer,
  MediaRemovePreviewButton,
} from './MediaComponents.style';
import {ImageCropModal} from './ImageCropModal';
import {garantirPath, podeRecortar} from './imageCrop';

const REQUEST_PRESIGNED_UPLOAD = gql`
  mutation RequestPresignedUpload(
    $filename: String!
    $contentType: String!
    $folder: String
  ) {
    requestPresignedUpload(
      input: {filename: $filename, contentType: $contentType, folder: $folder}
    ) {
      presignedUpload {
        url
        fields
        key
        fileUrl
      }
    }
  }
`;

type DirectUploadProps = {
  onUploadComplete: (url: string) => void;
  onUploadStatusChange?: (isUploading: boolean) => void;
  mediaType: 'image' | 'video' | 'audio' | 'attachment';
  folder?: string;
  customMessage?: string;
  children?: React.ReactNode;
  imagePreviewStyle?: React.CSSProperties;
  /** Proporcao fixa do recorte. 1 para avatar; sem valor, recorte livre. */
  cropAspect?: number;
  cropTitle?: string;
};

const ACCEPT_MEDIA = {
  image: 'image/jpeg, image/png, image/gif',
  video: 'video/*',
  audio: 'audio/*',
  attachment: 'application/zip, application/pdf',
};

const MAX_SIZE = {
  image: 10485760, // 10MB
  video: 943718400, // 900MB
  audio: 52428800, // 50MB
  attachment: 5242880, // 5MB
};

const ICON = {
  image: 'images',
  video: 'play',
  audio: 'podcast',
  attachment: 'file-pdf',
};

const UX_MESSAGE = {
  image: 'Clique para selecionar ou arraste sua imagem aqui.',
  video: 'Clique para selecionar ou arraste seu vídeo aqui.',
  audio: 'Clique para selecionar ou arraste seu áudio aqui.',
  attachment: 'Clique para selecionar ou arraste seu PDF aqui.',
};

export const DirectUpload = ({
  onUploadComplete,
  onUploadStatusChange,
  mediaType = 'image',
  folder,
  customMessage,
  children,
  imagePreviewStyle,
  cropAspect,
  cropTitle,
}: DirectUploadProps): JSX.Element => {
  const [files, setFiles] = React.useState<any[]>([]);
  const [paraRecortar, setParaRecortar] = React.useState<File | null>(null);
  const [errors, setErrors] = React.useState('');
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [isUploading, setIsUploading] = React.useState(false);

  const onUploadStatusChangeRef = React.useRef(onUploadStatusChange);
  const prevIsUploadingRef = React.useRef<boolean | null>(null);

  React.useEffect(() => {
    onUploadStatusChangeRef.current = onUploadStatusChange;
  }, [onUploadStatusChange]);

  React.useEffect(() => {
    return () => {
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    };
  }, [files]);

  React.useEffect(() => {
    if (prevIsUploadingRef.current !== isUploading) {
      prevIsUploadingRef.current = isUploading;
      onUploadStatusChangeRef.current?.(isUploading);
    }
  }, [isUploading]);

  const uploadToS3 = async (file: File, presignedData: any) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      console.log('Starting upload with data:', presignedData);

      // Verificar se estamos trabalhando com LocalStack
      const isLocalstack =
        presignedData.url.includes('localhost:4566') ||
        presignedData.url.includes('localstack:4566');

      // Ajustar URL para o navegador se necessário
      let uploadUrl = presignedData.url;
      if (uploadUrl.includes('localstack:4566')) {
        uploadUrl = uploadUrl.replace('localstack:4566', 'localhost:4566');
        console.log('Adjusted URL for browser access:', uploadUrl);
      }

      console.log('Upload target URL:', uploadUrl);
      console.log('Original URL:', presignedData.url);
      console.log('Is LocalStack:', isLocalstack);
      console.log('Content type:', file.type);

      // Configurar o controle de progresso do upload
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100,
          );
          setUploadProgress(percentComplete);
        }
      };

      xhr.onload = () => {
        console.log('Upload finished with status:', xhr.status);
        console.log('Response:', xhr.responseText);

        if (xhr.status >= 200 && xhr.status < 300) {
          onUploadComplete(presignedData.fileUrl);
          setIsUploading(false);
        } else {
          console.error(
            'Upload failed with status:',
            xhr.status,
            xhr.statusText,
          );
          console.error('Response body:', xhr.responseText);
          setErrors(`Falha no upload: ${xhr.status} - ${xhr.statusText}`);
          setIsUploading(false);
        }
      };

      xhr.onerror = (e) => {
        console.error('XHR error:', e);
        setErrors('Erro de rede ou servidor');
        setIsUploading(false);
      };

      xhr.open('PUT', uploadUrl);

      // Aplicar os cabeçalhos retornados pelo backend
      xhr.setRequestHeader('Content-Type', file.type);

      // Aplicar os cabeçalhos fornecidos pela URL pré-assinada
      if (presignedData.fields && typeof presignedData.fields === 'object') {
        Object.entries(presignedData.fields).forEach(
          ([headerName, headerValue]) => {
            if (headerValue !== null && headerValue !== undefined) {
              xhr.setRequestHeader(headerName, headerValue.toString());
              console.log(`Setting header: ${headerName}=${headerValue}`);
            }
          },
        );
      }

      console.log('Sending request...');
      xhr.send(file);
    } catch (error) {
      console.error('Upload error:', error);
      setErrors('Erro ao fazer upload: ' + (error as Error).message);
      setIsUploading(false);
    }
  };

  const handleUpload = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    try {
      const {data} = await client('account').mutate({
        mutation: REQUEST_PRESIGNED_UPLOAD,
        variables: {
          filename: file.name,
          contentType: file.type,
          folder: folder,
        },
      });

      await uploadToS3(file, data.requestPresignedUpload.presignedUpload);
    } catch (error) {
      setErrors('Erro ao obter URL para upload: ' + (error as Error).message);
    }
  };

  // Sem `path` a legenda com nome e tamanho some do preview, e o arquivo que sai
  // do recorte nasce sem ele.
  const aceitarArquivo = async (arquivo: File) => {
    setFiles([
      Object.assign(garantirPath(arquivo), {
        preview: URL.createObjectURL(arquivo),
      }),
    ]);

    await handleUpload([arquivo]);
  };

  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: ACCEPT_MEDIA[mediaType],
    maxFiles: 1,
    maxSize: MAX_SIZE[mediaType],
    onDrop: async (acceptedFiles) => {
      const arquivo = acceptedFiles[0];
      if (!arquivo) return;

      // Toda imagem passa pelo recorte antes de subir; o que o usuario enquadra
      // e o que vai para o S3.
      if (podeRecortar(arquivo.type)) {
        setParaRecortar(arquivo);
        return;
      }

      await aceitarArquivo(arquivo);
    },
  });

  const removeAll = () => {
    acceptedFiles.length = 0;
    acceptedFiles.splice(0, acceptedFiles.length);
    setFiles([]);
    setUploadProgress(0);
  };

  React.useEffect(() => {
    if (
      errors.length &&
      typeof window !== 'undefined' &&
      (window as any).M?.toast
    ) {
      (window as any).showToast({html: errors});
    }
  }, [errors]);

  React.useEffect(() => {
    if (fileRejections.length) {
      const firstRejection = fileRejections[0];
      if (firstRejection?.errors?.[0]) {
        const message = TranslateErrorDropzone(
          firstRejection.errors[0],
          mediaType,
        );
        setErrors(message);
      }
    }
  }, [fileRejections, mediaType]);

  const renderUploadProgress = () => {
    if (!isUploading) return null;

    return (
      <div className="progress">
        <div
          className="determinate"
          style={{width: `${uploadProgress}%`}}></div>
      </div>
    );
  };

  const MediaPreviewImage = (file: any) => {
    const defaultImageStyle = {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
      borderRadius: '4px',
    };

    return (
      <MediaPreview>
        <img
          src={file.preview}
          style={{...defaultImageStyle, ...imagePreviewStyle}}
        />
        {!!file?.path && !!file.size && (
          <MediaPreviewSubtitle>
            {file.path} - {humanFileSize(file.size)}
            <MediaRemovePreviewButton
              onClick={removeAll}
              title="Remover arquivo"
            />
          </MediaPreviewSubtitle>
        )}
        {renderUploadProgress()}
      </MediaPreview>
    );
  };

  const MediaPreviewVideo = (file: any) => {
    return (
      <>
        <MediaPlayer src={file.preview || file.uri} mime={file.type} />
        {!!file?.path && !!file.size && (
          <MediaPreviewSubtitle>
            {file.path} - {humanFileSize(file.size)}
            <MediaRemovePreviewButton
              onClick={removeAll}
              title="Remover arquivo"
            />
          </MediaPreviewSubtitle>
        )}
        {renderUploadProgress()}
      </>
    );
  };

  const MediaPreviewAudio = (file: any) => {
    return (
      <>
        <MediaPlayer src={file.preview || file.uri} mime={file.type} kind="audio" />
        {!!file?.path && !!file.size && (
          <MediaPreviewSubtitle>
            {file.path} - {humanFileSize(file.size)}
            <MediaRemovePreviewButton
              onClick={removeAll}
              title="Remover arquivo"
            />
          </MediaPreviewSubtitle>
        )}
        {renderUploadProgress()}
      </>
    );
  };

  const MediaPreviewPdf = (file: any) => {
    return (
      <>
        <MediaPreview>
          <object data={file.preview} width="100%" height="100%"></object>
        </MediaPreview>
        {!!file?.path && !!file.size && (
          <MediaPreviewSubtitle>
            {file.path} - {humanFileSize(file.size)}
            <MediaRemovePreviewButton
              onClick={removeAll}
              title="Remover arquivo"
            />
          </MediaPreviewSubtitle>
        )}
        {renderUploadProgress()}
      </>
    );
  };

  const MEDIA_PREVIEW = {
    image: (file: any) => MediaPreviewImage(file),
    video: (file: any) => MediaPreviewVideo(file),
    audio: (file: any) => MediaPreviewAudio(file),
    attachment: (file: any) => MediaPreviewPdf(file),
  };

  const thumbs = React.useMemo(
    () =>
      files.map((file, index) => (
        <div key={file?.path || index}>
          {MEDIA_PREVIEW[mediaType](file)}
          {!!children && <span>{customMessage || UX_MESSAGE[mediaType]}</span>}
        </div>
      )),
    [files, mediaType, children, isUploading, uploadProgress],
  );

  const renderEmptyPreview = () => {
    if (children) return children;

    return (
      <MediaPreview className="empty-preview">
        <i className={`fal fa-${ICON[mediaType]}`}></i>
        <span>{customMessage || UX_MESSAGE[mediaType]}</span>
      </MediaPreview>
    );
  };

  return (
    <MediaGroupContainer>
      <MediaPreviewContainer
        {...getRootProps({isDragActive, isDragAccept, isDragReject})}
        className="media-preview">
        <section>
          {files.length ? thumbs : renderEmptyPreview()}
          <input {...getInputProps()} />
        </section>
      </MediaPreviewContainer>

      {paraRecortar && (
        <ImageCropModal
          arquivo={paraRecortar}
          proporcao={cropAspect}
          titulo={cropTitle}
          onCancel={() => setParaRecortar(null)}
          onConfirm={(recortado) => {
            setParaRecortar(null);
            aceitarArquivo(recortado);
          }}
        />
      )}
    </MediaGroupContainer>
  );
};
